import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { left_arrow_icon } from '../../assets/icons/icons';
import {
  Container,
  ThemeTable,
  ThemeDropDown,
  ThemeSelect,
  DogByIDModal,
  UserDetailModal,
} from '../../components/components';
import { useGetLogsQuery } from '../../services/stats';
import { formatTimeET, formatDateET } from '../../helpers/helpers';
import { useLazyGetDogByIDQuery } from '../../services/dogsApi';
import {
  useCheckinAppointmentMutation,
  useCheckoutAppointmentMutation,
} from '../../services/appointments';
import { APPOINTMENT_STATUS } from '../../constants/constants';

const Logs = () => {
  const [logType, setLogType] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [isOpenUserModal, setIsOpenUserModal] = useState(false);
  const [resultPerPage, setResultPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [dogData, setDogData] = useState({});
  const [userData, setUserData] = useState({});
  const {
    data: logs,
    isLoading: fetchingLogs,
    refetch,
  } = useGetLogsQuery({
    currentPage,
    resultPerPage,
    type:
      logType === 'All'
        ? 'appointment_accepted_updated,checked_in,checked_out'
        : logType,
  });
  const [getDogByID, { isLoading: fetchingdog }] = useLazyGetDogByIDQuery();
  const [checkinAppointment, { isLoading: checkingin }] =
    useCheckinAppointmentMutation();
  const [checkoutAppointment, { isLoading: checkingout }] =
    useCheckoutAppointmentMutation();

  useEffect(() => {
    refetch();
  }, [logType]);

  useEffect(() => {
    setTotal(logs?.data?.total || 0);
  }, [logs]);

  const onClose = () => {
    setIsOpen(false);
    setDogData({});
  };

  const onOpenModal = async id => {
    setIsOpen(true);
    const response = await getDogByID(id);
    setDogData(response?.data?.data);
  };

  const onOpenUserModal = data => {
    setUserData(data);
    setIsOpenUserModal(true);
  };

  const onCloseUserModal = () => {
    setIsOpenUserModal(false);
    setUserData({});
  };

  const types = [
    {
      value: 'appointment_accepted_updated,checked_in,checked_out',
      key: 'accepted,checked_in,checked_out',
      label: 'All',
    },
    { value: 'login', key: 'login', label: 'login' },
    { value: 'social_login', key: 'social_login', label: 'Social Login' },
    {
      value: 'appointment_rejected_updated',
      key: 'appointment_rejected_updated',
      label: 'Appointment Rejected',
    },
    {
      value: 'appointment_accepted_updated',
      key: 'appointment_rejected_updated',
      label: 'Appointment Accepted',
    },
    {
      value: 'appointment_created',
      key: 'appointment_created',
      label: 'Appointment Created',
    },
    {
      value: 'appointment_update',
      key: 'appointment_update',
      label: 'Appointment Update',
    },
    {
      value: APPOINTMENT_STATUS.CHECKED_OUT,
      key: APPOINTMENT_STATUS.CHECKED_OUT,
      label: 'Checked Out',
    },
    {
      value: APPOINTMENT_STATUS.CHECKED_IN,
      key: APPOINTMENT_STATUS.CHECKED_IN,
      label: 'Checked In',
    },
    { value: 'dog_updated', key: 'dog_updated', label: 'Dog Updated' },
  ];

  const getTypeLabel = text => {
    const matchingType = types.find(type => type.value === text);
    return matchingType ? matchingType.label : text;
  };

  const updateStatus = async (e, id) => {
    let response;
    if (e === APPOINTMENT_STATUS.CHECKED_IN) {
      response = await checkinAppointment({ id, body: { status: e } });
    } else if (e === APPOINTMENT_STATUS.CHECKED_OUT) {
      response = await checkoutAppointment({ id, body: { status: e } });
    }
    refetch();
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: text => (
        <p className="text-center font-medium">{getTypeLabel(text)}</p>
      ),
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: text => (
        <p
          className="text-center font-medium cursor-pointer"
          onClick={() => onOpenUserModal(text)}
        >
          {text?.username}
        </p>
      ),
    },
    {
      title: 'Dog',
      dataIndex: 'dogs_details',
      key: 'dogs_details',
      render: text => (
        <div className="flex items-center justify-center">
          {text ? (
            <img
              className="cursor-pointer border-4 border-transparent hover:border-primary rounded-full object-cover w-10 h-10"
              src={text?.avatar}
              onClick={() => onOpenModal(text?._id)}
            />
          ) : (
            '---'
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, data) => (
        <div className="flex justify-center">
          <ThemeSelect
            value={data?.appointment_details?.status || '---'}
            options={
              data?.appointment_details?.status === APPOINTMENT_STATUS.ACCEPTED
                ? [
                    {
                      value: APPOINTMENT_STATUS.ACCEPTED,
                      label: APPOINTMENT_STATUS.ACCEPTED,
                    },
                    {
                      value: APPOINTMENT_STATUS.CHECKED_IN,
                      label: 'checked in',
                    },
                  ]
                : [
                    {
                      value: APPOINTMENT_STATUS.CHECKED_IN,
                      label: 'checked in',
                    },
                    {
                      value: APPOINTMENT_STATUS.CHECKED_OUT,
                      label: 'checked out',
                    },
                  ]
            }
            className={`!w-32 rounded-md`}
            disabled={
              (data?.appointment_details?.status === APPOINTMENT_STATUS.ACCEPTED
                ? checkingin
                : checkingout) ||
              !data?.appointment_details?.status ||
              data?.appointment_details?.status ===
                APPOINTMENT_STATUS.CHECKED_OUT
            }
            onChange={e => updateStatus(e, data?.appointment_id)}
          />
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: text => (
        <p className="text-center font-medium">
          {formatDateET(text, 'ddd MMM D, YYYY')}
        </p>
      ),
    },
    {
      title: 'Time (ET)',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: text => (
        <p className="text-center font-medium">
          {formatTimeET(text, 'h:mm:ss A')}
        </p>
      ),
    },
  ];

  return (
    <Container
      selected={'1'}
      header={
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <NavLink to="/dashboard">
              <img src={left_arrow_icon} width={18} height={18} alt="" />
            </NavLink>
            <p className="ml-3 font-semibold text-2xl">Logs</p>
          </div>
          <div>
            <ThemeDropDown
              className="mr-2"
              setValue={e => setLogType(e)}
              text={
                (logType === 'accepted,checked_in,checked_out' && 'All') ||
                logType
              }
              items={types}
            />
          </div>
        </div>
      }
    >
      <ThemeTable
        pagination={{ total, setCurrentPage, setResultPerPage }}
        columns={columns}
        data={logs?.data?.logs || []}
        loader={fetchingLogs}
        entityName="activity"
        emptyText={
          logType === 'All'
            ? 'No activity recorded yet. Check-ins, check-outs and appointment updates show up here.'
            : `No "${logType}" activity recorded yet.`
        }
      />
      <DogByIDModal
        isOpen={isOpen}
        onClose={onClose}
        data={dogData}
        isLoading={fetchingdog}
      />
      <UserDetailModal
        isOpen={isOpenUserModal}
        onClose={onCloseUserModal}
        data={userData}
      />
    </Container>
  );
};

export default Logs;
